import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from 'recharts';
import { ArrowLeft, Bot, ChevronDown, Copy, CreditCard, Download, FileText, MessageSquare, Power, Receipt, Users } from 'lucide-react';
import { Page, PageHeader } from '@ampm-7en-ai/ui/components/Page';
import { Panel } from '@ampm-7en-ai/ui/components/Panel';
import { ListCard, ListRow, ListRowSkeleton } from '@ampm-7en-ai/ui/components/ListRow';
import { EmptyState } from '@ampm-7en-ai/ui/components/EmptyState';
import { Button } from '@ampm-7en-ai/ui/components/Button';
import { Pill } from '@ampm-7en-ai/ui/components/StatusBadge';
import { Avatar } from '@ampm-7en-ai/ui/components/Avatar';
import { IconChip } from '@ampm-7en-ai/ui/components/IconChip';
import { SegmentedControl, type SegmentedOption } from '@ampm-7en-ai/ui/components/SegmentedControl';
import { Select } from '@ampm-7en-ai/ui/components/Select';
import { DetailRow, DetailRows } from '@ampm-7en-ai/ui/components/DetailList';
import { Callout } from '@ampm-7en-ai/ui/components/Callout';
import { Skeleton } from '@ampm-7en-ai/ui/components/Skeleton';
import { ConfirmDialog } from '@ampm-7en-ai/ui/components/ConfirmDialog';
import { ChartEmpty, ChartTooltip } from '@ampm-7en-ai/ui/components/Chart';
import { axisProps, gridProps, lineCursor, tooltipCursor } from '@ampm-7en-ai/ui/components/chartStyle';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/utils/api-interceptor';
import { getApiUrl } from '@/utils/api-config';
import { formatRelativeTime } from '@/lib/utils';
import { costMoney, money, toAmount } from '@/lib/money';
import { isRecord } from '@/lib/records';
import { useBusinessDetail, type Agent, type CostVsSubscriptionView } from '@/hooks/useBusinesses';

/**
 * Everything about one business: who is in it, what it has built, what it
 * costs to run, and what it has paid.
 *
 * The order is deliberate — what it is, then what it does, then what that
 * costs — because the reason a superadmin opens this page is almost always
 * the last of those, and the first two are the context for reading it.
 */

const TRANSACTION_PAGE_SIZE = 10;

const CARD_LOGOS: Record<string, { src: string; label: string }> = {
  visa: { src: 'https://upload.wikimedia.org/wikipedia/commons/1/12/Font_Awesome_5_brands_cc-visa.svg', label: 'Visa' },
  mastercard: { src: 'https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg', label: 'Mastercard' },
  amex: { src: 'https://upload.wikimedia.org/wikipedia/commons/3/30/American_Express_logo.svg', label: 'American Express' },
  discover: { src: 'https://upload.wikimedia.org/wikipedia/commons/e/e5/DiscoverCard.svg', label: 'Discover' },
};

function CardBrandIcon({ brand }: { brand: string }) {
  const [failed, setFailed] = React.useState(false);
  const normalized = brand.toLowerCase().replace(/[\s_-]+/g, '');
  const logo = CARD_LOGOS[normalized === 'americanexpress' ? 'amex' : normalized];
  if (!logo || failed) return <CreditCard className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />;
  return <img src={logo.src} alt={`${logo.label} card`} className="h-5 w-8 shrink-0 rounded-sm p-0.5 object-contain dark:invert"
    loading="lazy" referrerPolicy="no-referrer" onError={() => {
      console.error(`Could not load ${logo.label} card logo.`);
      setFailed(true);
    }} />;
}

type TransactionRecord = {
  payment_date?: string | null;
  transaction_date?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  amount?: number | string | null;
  status?: string | null;
  invoice_url?: string | null;
  plan_name?: string | null;
  addon_type?: string | null;
  quantity?: number | null;
  payment_type?: string | null;
  card_last4?: string | number | null;
  card_brand?: string | null;
  transaction_id?: string | number | null;
};

/* ------------------------------------------------------------------ *
 * Reading the numbers
 * ------------------------------------------------------------------ */

const toNumber = toAmount;

const when = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('') || '?';

/** Status words differ by endpoint; this is the one judgement about them. */
const isGood = (status?: string | null) => {
  const word = (status ?? '').toLowerCase();
  return word === 'active' || word === 'enabled' || word === 'paid' || word === 'succeeded' || word === 'success';
};

/**
 * Narrower than `isGood`: only a charge still running has a period worth
 * showing. A paid or refunded one has an end date that has already passed.
 */
const isActive = (status?: string | null) => (status ?? '').trim().toLowerCase() === 'active';

/** Cost maps arrive keyed by name, with values as numbers or numeric strings. */
const costRows = (source?: Record<string, number | string | null>) =>
  Object.entries(source ?? {})
    .map(([name, value]) => ({ name, value: toNumber(value) }))
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value);

const tokenCount = (value: unknown): number | null => {
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  if (typeof value === 'string' && !value.trim()) return null;
  const count = Number(value);
  return Number.isFinite(count) && count >= 0 ? count : null;
};

function ConversationTooltip({ active, payload }: TooltipProps<number, string>) {
  const point: unknown = payload?.[0]?.payload;
  if (!active || !isRecord(point)) return null;
  const conversations = toNumber(point.no_of_conversations);
  const tokens = tokenCount(point.no_of_tokens);
  return (
    <div className="rounded-ui-sm border border-ui-border bg-ui-surface px-3 py-2 text-ui-xs text-ui-fg shadow-ui-pop">
      <p className="mb-2 font-medium">{typeof point.week === 'string' ? point.week : 'Period'}</p>
      <dl className="space-y-1">
        <div className="flex justify-between gap-4"><dt>Conversations</dt><dd>{conversations.toLocaleString()}</dd></div>
        <div className="flex justify-between gap-4"><dt>Tokens</dt><dd>{tokens === null ? 'Unavailable' : tokens.toLocaleString()}</dd></div>
        <div className="flex justify-between gap-4"><dt>Avg tokens/conversation</dt>
          <dd>{tokens === null || conversations === 0 ? 'N/A' : (tokens / conversations).toLocaleString(undefined, { maximumFractionDigits: 2 })}</dd>
        </div>
      </dl>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Pieces
 * ------------------------------------------------------------------ */

function Stat({ icon: Icon, label, value, loading }: { icon: typeof Bot; label: string; value: string; loading?: boolean }) {
  return (
    <Panel padding="md" className="flex items-start gap-3">
      <IconChip icon={Icon} tone="accent" />
      <div className="min-w-0">
        <p className="text-ui-xs text-ui-fg-muted">{label}</p>
        {loading ? <Skeleton className="mt-1.5 h-6 w-14" /> : <p className="mt-0.5 text-ui-xl text-ui-fg">{value}</p>}
      </div>
    </Panel>
  );
}

function AgentRow({ agent }: { agent: Agent }) {
  const [expanded, setExpanded] = React.useState(false);
  const sources = agent.knowledge_sources ?? [];
  const characters = sources.reduce((total, source) => total + toNumber(source.extracted_characters), 0);
  const panelId = React.useId();

  return (
    <li>
      <div className="space-y-4 p-4">
        <div className="flex items-start gap-3">
          <IconChip icon={Bot} tone={isGood(agent.status) ? 'accent' : 'neutral'} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="min-w-0 break-words text-ui-sm font-medium text-ui-fg">{agent.name}</h3>
            <Pill tone={isGood(agent.status) ? 'success' : 'neutral'}>{agent.status}</Pill>
            </div>
            <p className="mt-1 break-words text-ui-xs text-ui-fg-muted">
            {agent.agent_category || 'Uncategorized'}{agent.agentType ? ` · ${agent.agentType.replace(/_/g, ' ')}` : ''}
            </p>
          </div>
        </div>
        <dl className="grid grid-cols-1 gap-3 rounded-ui-sm bg-ui-subtle p-3 sm:grid-cols-3">
          <div>
            <dt className="text-ui-xs text-ui-fg-muted">Characters</dt>
            <dd className="mt-1 break-words text-ui-sm tabular-nums text-ui-fg">{characters.toLocaleString()} chars</dd>
          </div>
          <div>
            <dt className="text-ui-xs text-ui-fg-muted">Conversations</dt>
            <dd className="mt-1 break-words text-ui-sm tabular-nums text-ui-fg">{agent.conversations.toLocaleString()} conversations</dd>
          </div>
          <div>
            <dt className="text-ui-xs text-ui-fg-muted">Knowledge sources</dt>
            <dd className="mt-1 text-ui-sm tabular-nums text-ui-fg">{sources.length.toLocaleString()}</dd>
          </div>
        </dl>
        {sources.length > 0 ? (
          <Button variant="ghost" size="sm" className="max-w-full" aria-label={`Knowledge sources for ${agent.name}`}
            aria-expanded={expanded} aria-controls={panelId} onClick={() => setExpanded(!expanded)}>
            {expanded ? 'Hide knowledge sources' : 'View knowledge sources'} <ChevronDown className={expanded ? 'rotate-180' : ''} />
          </Button>
        ) : <p className="text-ui-xs text-ui-fg-subtle">No knowledge sources</p>}
      </div>
      {expanded && (
        <div id={panelId} role="region" aria-label={`Knowledge sources for ${agent.name}`}
          className="border-t border-ui-border px-4 py-3">
          <ul className="space-y-3">
            {sources.map((source, index) => (
              <li key={index} className="grid gap-1 sm:grid-cols-[minmax(0,1fr)_auto] sm:gap-3">
                <div className="min-w-0">
                  <p className="break-words text-ui-sm text-ui-fg">{source.title || 'Untitled source'}</p>
                  <p className="text-ui-xs text-ui-fg-muted">Type: {source.type || '—'}</p>
                </div>
                <span className="shrink-0 text-ui-xs text-ui-fg-muted">
                  {toNumber(source.extracted_characters).toLocaleString()} chars
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  );
}

function Section({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-6">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-ui-lg font-normal text-ui-fg">{title}</h2>
          {description && <p className="mt-0.5 text-ui-sm text-ui-fg-muted">{description}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * The page
 * ------------------------------------------------------------------ */

export default function BusinessDetailV2() {
  const { businessId } = useParams();
  const { toast } = useToast();
  const { data, isLoading, isError, error, refetch } = useBusinessDetail(businessId);

  const [teamView, setTeamView] = React.useState<'team' | 'agents'>('team');
  const [costView, setCostView] = React.useState<'llms' | 'agents'>('llms');
  const [costVsKey, setCostVsKey] = React.useState('');
  const [confirmingToggle, setConfirmingToggle] = React.useState(false);
  const [toggling, setToggling] = React.useState(false);

  const info = data?.business_info;
  const stats = data?.account_statistics;
  const disabled = info?.status === 'Disabled';
  const copyTransactionId = async (id: string) => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard access is unavailable. Select the transaction ID to copy it manually.');
      await navigator.clipboard.writeText(id);
      toast({ title: 'Transaction ID copied' });
    } catch (error) {
      console.error('Could not copy transaction ID:', error);
      toast({
        title: 'Could not copy transaction ID',
        description: error instanceof Error ? error.message : 'Select the transaction ID to copy it manually.',
        variant: 'destructive',
      });
    }
  };
  const conversationData = data?.weekwise_conversation_volume ?? [];
  const conversationTotal = conversationData.reduce((sum, point) => sum + toNumber(point.no_of_conversations), 0);
  const tokensAvailable = conversationData.length > 0 && conversationData.every((point) => tokenCount(point.no_of_tokens) !== null);
  const tokenTotal = tokensAvailable ? conversationData.reduce((sum, point) => sum + (tokenCount(point.no_of_tokens) ?? 0), 0) : null;

  /* ---------------- Transactions ---------------- */

  const [transactions, setTransactions] = React.useState<TransactionRecord[]>([]);
  const [page, setPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);
  const [totalCount, setTotalCount] = React.useState(0);
  const [loadingTransactions, setLoadingTransactions] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);

  const fetchTransactions = React.useCallback(
    async (which: number, append = false) => {
      if (!businessId) return;
      if (append) setLoadingMore(true);
      else setLoadingTransactions(true);
      try {
        const response = await apiRequest(
          getApiUrl(`admin/businesses/${businessId}/transaction-history/?page=${which}&page_size=${TRANSACTION_PAGE_SIZE}`)
        );
        const payload = await response.json().catch(() => null);
        if (!response.ok) throw new Error(payload?.message || 'Couldn’t load the transactions.');

        const body = payload?.data ?? {};
        const rows: TransactionRecord[] = Array.isArray(body.transactions) ? body.transactions : [];
        setTransactions((current) => (append ? [...current, ...rows] : rows));
        // The API can return its default page field rather than the requested page.
        setPage(which);
        setTotalPages(body.total_pages || 1);
        setTotalCount(body.total_count ?? rows.length);
      } catch (failure) {
        toast({
          title: 'Couldn’t load the transactions',
          description: failure instanceof Error ? failure.message : 'Something went wrong.',
          variant: 'destructive',
        });
      } finally {
        if (append) setLoadingMore(false);
        else setLoadingTransactions(false);
      }
    },
    [businessId, toast]
  );

  React.useEffect(() => {
    if (businessId) void fetchTransactions(1);
  }, [businessId, fetchTransactions]);

  /* ---------------- Enable / disable ---------------- */

  const toggleBusiness = async () => {
    if (!businessId) return;
    setToggling(true);
    try {
      const action = disabled ? 'enable' : 'disable';
      const response = await apiRequest(getApiUrl(`admin/businesses/${businessId}/${action}/`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) throw new Error(payload?.error?.message || payload?.message || `Couldn’t ${action} this business.`);

      toast({ title: disabled ? 'Business enabled' : 'Business disabled' });
      setConfirmingToggle(false);
      await refetch();
    } catch (failure) {
      toast({
        title: 'That didn’t work',
        description: failure instanceof Error ? failure.message : 'Nothing changed.',
        variant: 'destructive',
      });
    } finally {
      setToggling(false);
    }
  };

  /* ---------------- Cost against what they pay ---------------- */

  const costVs = typeof data?.cost_vs_subscription_statistics === 'object' ? data.cost_vs_subscription_statistics : null;

  const costViews = React.useMemo<{ key: string; view: CostVsSubscriptionView }[]>(() => {
    const raw = costVs?.views;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.map((view, index) => ({ key: view.key ?? String(index), view }));
    return Object.entries(raw).map(([key, view]) => ({ key, view }));
  }, [costVs]);

  React.useEffect(() => {
    if (!costViews.length) return;
    const preferred = costVs?.default_view;
    const exists = costViews.some((one) => one.key === costVsKey);
    if (!exists) setCostVsKey(preferred && costViews.some((one) => one.key === preferred) ? preferred : costViews[0].key);
  }, [costViews, costVs?.default_view, costVsKey]);

  const activeView = costViews.find((one) => one.key === costVsKey)?.view;

  const costVsSeries = React.useMemo(() => {
    if (!activeView?.labels) return [];
    return activeView.labels.map((label, index) => ({
      label,
      cost: toNumber(activeView.operational_cost?.[index]),
      paid: toNumber(activeView.subscription?.[index]),
    }));
  }, [activeView]);

  const llmRows = costRows(data?.llm_costs?.llm_cost);
  const agentRows = costRows(data?.llm_costs?.agent_cost);
  const shownCosts = costView === 'llms' ? llmRows : agentRows;

  const COST_TABS: SegmentedOption<'llms' | 'agents'>[] = [
    { value: 'llms', label: 'By model' },
    { value: 'agents', label: 'By agent' },
  ];

  const TEAM_TABS: SegmentedOption<'team' | 'agents'>[] = [
    { value: 'team', label: `Team${data ? ` (${data.team?.length ?? 0})` : ''}` },
    { value: 'agents', label: `Agents${data ? ` (${data.agents?.length ?? 0})` : ''}` },
  ];

  /* ---------------- Render ---------------- */

  if (isError) {
    return (
      <Page>
        <Button variant="ghost" className="-ml-2 mb-3 gap-1.5" asChild>
          <Link to="/v2/businesses">
            <ArrowLeft /> All businesses
          </Link>
        </Button>
        <Callout tone="danger" title="Couldn’t load this business">
          {error instanceof Error ? error.message : 'Something went wrong fetching it.'}
        </Callout>
      </Page>
    );
  }

  return (
    <Page>
      <Button variant="ghost" className="-ml-2 mb-3 gap-1.5" asChild>
        <Link to="/v2/businesses">
          <ArrowLeft /> All businesses
        </Link>
      </Button>

      <PageHeader
        title={isLoading ? 'Loading…' : info?.name || 'Business'}
        description={info?.domain || undefined}
        meta={
          info ? (
            <Pill tone={disabled ? 'danger' : 'success'}>{disabled ? 'Disabled' : 'Enabled'}</Pill>
          ) : undefined
        }
        actions={
          info && (
            <Button
              variant={disabled ? 'primary' : 'secondary'}
              onClick={() => setConfirmingToggle(true)}
              disabled={toggling}
            >
              <Power /> {disabled ? 'Enable' : 'Disable'}
            </Button>
          )
        }
      />

      {disabled && (
        <Callout tone="warning" className="mb-6" title="This business is disabled">
          Nobody in it can sign in, and its agents don’t answer. Everything it has built is kept.
        </Callout>
      )}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat icon={Users} label="Team" value={String(data?.team?.length ?? 0)} loading={isLoading} />
        <Stat icon={Bot} label="Agents" value={String(stats?.agents ?? 0)} loading={isLoading} />
        <Stat icon={MessageSquare} label="Conversations" value={(stats?.conversations ?? 0).toLocaleString()} loading={isLoading} />
        <Stat icon={FileText} label="Documents" value={String(stats?.documents ?? 0)} loading={isLoading} />
      </div>

      {/* ---------------- Who they are ---------------- */}
      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Panel padding="md">
          <h2 className="mb-4 text-ui-base font-medium text-ui-fg">Business</h2>
          {isLoading ? (
            <Skeleton className="h-40 w-full rounded-ui-md" />
          ) : (
            <DetailRows>
              <DetailRow label="Email">{info?.email || '—'}</DetailRow>
              <DetailRow label="Phone">{info?.phone || '—'}</DetailRow>
              <DetailRow label="Domain">{info?.domain || '—'}</DetailRow>
              <DetailRow label="Sector">{info?.sector_activity || '—'}</DetailRow>
              <DetailRow label="Size">{info?.company_size || '—'}</DetailRow>
              <DetailRow label="Joined">{when(info?.account_created)}</DetailRow>
            </DetailRows>
          )}
        </Panel>

        <Panel padding="md">
          <h2 className="mb-4 text-ui-base font-medium text-ui-fg">Subscription</h2>
          {isLoading ? (
            <Skeleton className="h-40 w-full rounded-ui-md" />
          ) : (
            <DetailRows>
              <DetailRow label="Plan">{data?.subscription?.plan || '—'}</DetailRow>
              <DetailRow label="Status">
                {data?.subscription?.status ? (
                  <Pill tone={isGood(data.subscription.status) ? 'success' : 'warning'}>{data.subscription.status}</Pill>
                ) : (
                  '—'
                )}
              </DetailRow>
              <DetailRow label="Billing cycle">{data?.subscription?.billing_cycle || '—'}</DetailRow>
              <DetailRow label="Next bill">{when(data?.subscription?.next_billing_date)}</DetailRow>
            </DetailRows>
          )}
          {data?.recent_activities && (
            <div className="mt-4 border-t border-ui-border pt-4">
              <p className="mb-2 text-ui-xs font-medium uppercase tracking-wide text-ui-fg-subtle">Recently</p>
              <ul className="space-y-1 text-ui-xs text-ui-fg-muted">
                <li>Last signed in {formatRelativeTime(data.recent_activities.last_login) || '—'}</li>
                <li>Agent updated {formatRelativeTime(data.recent_activities.agent_updated) || '—'}</li>
                <li>Subscription changed {formatRelativeTime(data.recent_activities.subscription_changed) || '—'}</li>
              </ul>
            </div>
          )}
        </Panel>
      </div>

      {/* ---------------- What it does ---------------- */}
      <Section title="Conversations" description="Conversation and token usage over the last 30 days, grouped by week.">
        <Panel padding="md">
          {isLoading ? (
            <Skeleton className="h-[240px] w-full rounded-ui-md" />
          ) : conversationData.length === 0 ? (
            <ChartEmpty height={240} />
          ) : (
            <>
            <dl className="mb-5 grid gap-3 sm:grid-cols-3">
              <div className="rounded-ui-sm bg-ui-subtle p-3">
                <dt className="text-ui-xs text-ui-fg-muted">Total conversations</dt>
                <dd className="mt-1 text-ui-xl tabular-nums">{conversationTotal.toLocaleString()}</dd>
              </div>
              <div className="rounded-ui-sm bg-ui-subtle p-3">
                <dt className="text-ui-xs text-ui-fg-muted">Total tokens</dt>
                <dd className="mt-1 text-ui-xl tabular-nums">{tokenTotal === null ? 'Unavailable' : tokenTotal.toLocaleString()}</dd>
              </div>
              <div className="rounded-ui-sm bg-ui-subtle p-3">
                <dt className="text-ui-xs text-ui-fg-muted">Average tokens per conversation</dt>
                <dd className="mt-1 text-ui-xl tabular-nums">{tokenTotal === null || conversationTotal === 0
                  ? 'N/A' : (tokenTotal / conversationTotal).toLocaleString(undefined, { maximumFractionDigits: 2 })}</dd>
              </div>
            </dl>
            {!tokensAvailable && <p className="mb-3 text-ui-xs text-ui-fg-muted">Token usage was not provided for every period.</p>}
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={conversationData} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="bd-conversations" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--v2-brand))" stopOpacity={0.28} />
                    <stop offset="100%" stopColor="hsl(var(--v2-brand))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="week" {...axisProps} />
                <YAxis {...axisProps} width={52} allowDecimals={false} />
                <Tooltip cursor={lineCursor} content={<ConversationTooltip />} />
                <Area
                  type="monotone"
                  dataKey="no_of_conversations"
                  name="Conversations"
                  stroke="hsl(var(--v2-brand))"
                  strokeWidth={2}
                  fill="url(#bd-conversations)"
                />
              </AreaChart>
            </ResponsiveContainer>
            </>
          )}
        </Panel>
      </Section>

      <Section
        title="Team & Agents"
        description="Team members and all agents belonging to this business."
        action={<SegmentedControl size="sm" aria-label="Team & Agents" options={TEAM_TABS} value={teamView} onValueChange={setTeamView} />}
      >
        {isLoading ? (
          <ListCard>
            {Array.from({ length: 3 }, (_, index) => (
              <li key={index}>
                <ListRowSkeleton />
              </li>
            ))}
          </ListCard>
        ) : teamView === 'agents' ? (data?.agents ?? []).length === 0 ? (
          <Panel variant="dashed">
            <EmptyState icon={Bot} title="No agents yet" description="This business hasn’t built anything to answer with." />
          </Panel>
        ) : (
          <ListCard>
            {(data?.agents ?? []).map((agent) => (
              <AgentRow key={`${businessId}-${agent.id}`} agent={agent} />
            ))}
          </ListCard>
        ) : (data?.team ?? []).length === 0 ? (
          <Panel variant="dashed">
            <EmptyState icon={Users} title="No team members" description="Nobody else has been invited in." />
          </Panel>
        ) : (
          <ListCard>
            {(data?.team ?? []).map((member) => (
              <li key={member.id}>
                <ListRow
                  leading={<Avatar initials={initials(member.username)} />}
                  title={<span className="inline-flex max-w-full items-center gap-2">
                    <span className="truncate">{member.username}</span>
                    <Pill>{member.team_role === null || member.team_role === undefined || member.team_role === ''
                      ? 'Not assigned'
                      : typeof member.team_role === 'number' ? `Role ${member.team_role}` : member.team_role}</Pill>
                  </span>}
                  description={<span className="font-ui-mono text-ui-xs text-ui-fg-subtle">{member.email}</span>}
                />
              </li>
            ))}
          </ListCard>
        )}
      </Section>

      {/* ---------------- What it costs ---------------- */}
      <Section
        title="Running cost"
        description="What this business costs the platform to serve."
        action={<SegmentedControl size="sm" aria-label="Cost breakdown" options={COST_TABS} value={costView} onValueChange={setCostView} />}
      >
        <Panel padding="md">
          {isLoading ? (
            <Skeleton className="h-[240px] w-full rounded-ui-md" />
          ) : shownCosts.length === 0 ? (
            <ChartEmpty height={240}>Nothing has been spent on {costView === 'llms' ? 'models' : 'agents'} yet.</ChartEmpty>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(200, shownCosts.length * 44)}>
              <BarChart data={shownCosts} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                <CartesianGrid {...gridProps} horizontal={false} vertical />
                <XAxis type="number" {...axisProps} tickFormatter={costMoney} />
                <YAxis type="category" dataKey="name" {...axisProps} width={140} />
                <Tooltip
                  cursor={tooltipCursor}
                  formatter={(value) => costMoney(value)}
                  contentStyle={{
                    backgroundColor: 'hsl(var(--v2-surface))',
                    borderColor: 'hsl(var(--v2-border))',
                    borderRadius: 'var(--ui-radius-sm, 8px)',
                    fontSize: 12,
                  }}
                  labelStyle={{ color: 'hsl(var(--v2-fg))' }}
                  itemStyle={{ color: 'hsl(var(--v2-fg))' }}
                />
                <Bar dataKey="value" name="Cost" fill="hsl(var(--v2-brand))" radius={[0, 6, 6, 0]} maxBarSize={22} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>
      </Section>

      {costVs && (
        <Section
          title="Cost against what they pay"
          description="Compare the cost of serving this business with its charges."
          action={
            costViews.length > 1 && (
              <Select
                aria-label="Period"
                size="sm"
                className="w-40"
                options={costViews.map(({ key, view }) => ({ value: key, label: view.label ?? key }))}
                value={costVsKey}
                onValueChange={setCostVsKey}
              />
            )
          }
        >
          <Panel padding="md">
            {costVs.summary && (
              <div className="mb-5 grid gap-4 sm:grid-cols-3">
                {(['day', 'month', 'year'] as const).map((period) => {
                  const cost = costVs.summary?.operational_cost?.[period];
                  const paid = costVs.summary?.subscription?.[period];
                  if (cost === undefined && paid === undefined) return null;
                  const difference = toNumber(paid) - toNumber(cost);
                  return (
                    <div key={period} className="rounded-ui-md border border-ui-border p-3">
                      <p className="text-ui-xs uppercase tracking-wide text-ui-fg-subtle">Per {period}</p>
                      <p className="mt-1 text-ui-lg text-ui-fg">{money(paid)}</p>
                      <p className="text-ui-xs text-ui-fg-muted">costs {money(cost)}</p>
                      <p className={`mt-1 text-ui-xs ${difference >= 0 ? 'text-ui-success-fg' : 'text-ui-danger'}`}>
                        {difference >= 0 ? 'margin ' : 'loss '}
                        {money(Math.abs(difference))}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}

            {costVsSeries.length === 0 ? (
              <ChartEmpty height={240} />
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={costVsSeries} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="label" {...axisProps} />
                  <YAxis {...axisProps} width={52} tickFormatter={(value: number) => money(value, { short: true, decimals: 0 })} />
                  <Tooltip cursor={tooltipCursor} content={<ChartTooltip unit="€" />} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                  <Bar dataKey="paid" name="Billed" fill="hsl(var(--v2-brand))" radius={[6, 6, 0, 0]} maxBarSize={28} />
                  <Bar dataKey="cost" name="Cost" fill="hsl(var(--v2-accent))" radius={[6, 6, 0, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Panel>
        </Section>
      )}

      {/* ---------------- What they have paid ---------------- */}
      <Section
        title="Transactions"
        description={totalCount > 0 ? `${totalCount.toLocaleString()} in total.` : 'Everything this business has been charged.'}
      >
        {loadingTransactions ? (
          <ListCard>
            {Array.from({ length: 4 }, (_, index) => (
              <li key={index}>
                <ListRowSkeleton />
              </li>
            ))}
          </ListCard>
        ) : transactions.length === 0 ? (
          <Panel variant="dashed">
            <EmptyState icon={Receipt} title="Nothing charged yet" description="Payments appear here as they are taken." />
          </Panel>
        ) : (
          <>
            <ListCard>
              {transactions.map((entry, index) => {
                const card = entry.card_last4 === null || entry.card_last4 === undefined
                  ? '' : String(entry.card_last4).slice(-4);
                const transactionId = entry.transaction_id === null || entry.transaction_id === undefined
                  ? '' : String(entry.transaction_id);
                return (
                  <li key={`${entry.transaction_id ?? 'row'}-${index}`}>
                    <ListRow
                      leading={<IconChip icon={Receipt} tone={isGood(entry.status) ? 'accent' : 'neutral'} />}
                      title={<span className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                        <span className="truncate">
                          {entry.plan_name || entry.addon_type || entry.payment_type || 'Payment'}
                          {entry.quantity ? <span className="ml-1.5 text-ui-xs font-normal text-ui-fg-subtle">×{entry.quantity}</span> : null}
                        </span>
                        {card && <span className="inline-flex items-center gap-0.5 text-ui-xs font-normal text-ui-fg-muted">
                          <CardBrandIcon key={entry.card_brand} brand={entry.card_brand ?? ''} />
                          {card}
                        </span>}
                      </span>}
                      description={
                        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span className="text-ui-xs text-ui-fg-subtle">
                            {when(entry.payment_date ?? entry.transaction_date)}
                          </span>
                          {entry.end_date && isActive(entry.status) && (
                            <>
                              <span aria-hidden="true">·</span>
                              {/* Only the end of the billed period: the start is the charge date above. */}
                              <span className="text-ui-xs text-ui-fg-subtle">Ends {when(entry.end_date)}</span>
                            </>
                          )}
                          <span aria-hidden="true">·</span>
                          {transactionId.trim() ? (
                            <span className="inline-flex min-w-0 max-w-full items-center gap-1">
                              <code className="min-w-0 break-all font-ui-mono text-ui-xs text-ui-fg">{transactionId}</code>
                              <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0"
                                aria-label={`Copy transaction ID ${transactionId}`}
                                onClick={() => void copyTransactionId(transactionId)}>
                                <Copy />
                              </Button>
                            </span>
                          ) : <span>Transaction ID unavailable</span>}
                          {entry.invoice_url && (
                            // Pushed right so it lines up under the amount, clear of the ID.
                            <Button size="icon" variant="ghost" className="ml-auto h-7 w-7 shrink-0" asChild>
                              <a href={entry.invoice_url} target="_blank" rel="noreferrer noopener"
                                aria-label={`Download invoice${transactionId.trim() ? ` for ${transactionId}` : ''} (opens in a new tab)`}>
                                <Download />
                              </a>
                            </Button>
                          )}
                        </span>
                      }
                      meta={
                        <span className="flex items-center gap-2">
                          <span className="text-ui-sm text-ui-fg">{money(entry.amount)}</span>
                          {entry.status && (
                            <Pill tone={isGood(entry.status) ? 'success' : 'warning'}>{entry.status}</Pill>
                          )}
                        </span>
                      }
                    />
                  </li>
                );
              })}
            </ListCard>

            {(page < totalPages || transactions.length > TRANSACTION_PAGE_SIZE) && (
              <div className="mt-3 flex items-center justify-between gap-3">
                <p className="text-ui-xs text-ui-fg-subtle">
                  {transactions.length} of {totalCount.toLocaleString()}
                </p>
                <div className="flex items-center gap-2">
                  {transactions.length > TRANSACTION_PAGE_SIZE && (
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setTransactions((current) => current.slice(0, TRANSACTION_PAGE_SIZE));
                        setPage(1);
                      }}
                    >
                      Show less
                    </Button>
                  )}
                  {page < totalPages && (
                    <Button variant="secondary" disabled={loadingMore} onClick={() => void fetchTransactions(page + 1, true)}>
                      {loadingMore ? 'Loading…' : 'Show more'}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </Section>

      <ConfirmDialog
        open={confirmingToggle}
        onOpenChange={(open) => !open && !toggling && setConfirmingToggle(false)}
        title={disabled ? `Enable ${info?.name ?? 'this business'}?` : `Disable ${info?.name ?? 'this business'}?`}
        description={
          disabled
            ? 'Everyone in it can sign in again and its agents start answering.'
            : 'Nobody in it will be able to sign in and its agents stop answering. Nothing is deleted, and you can enable it again.'
        }
        confirmLabel={toggling ? 'Working…' : disabled ? 'Enable' : 'Disable'}
        tone={disabled ? 'default' : 'danger'}
        onConfirm={toggleBusiness}
      />
    </Page>
  );
}
